import React, { useEffect, useState } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, FormControlLabel, IconButton, Radio,
    RadioGroup, Stack, Table, TableBody, TableCell, TableHead,
    TableRow, TextField, Typography, Chip, Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { ExpenseClaimService } from '../../services/expense-claim.service';
import { ExpenseCategoryService } from '../../services/expense-category.service';
import {
    ExpenseCategoryDTO,
    ExpenseClaimDTO,
    ReimbursementMethod,
} from '../../interfaces/expense.interface';
import { useToast } from '../../context/ToastContext';
import { useEmployeeNames } from '../../hooks/useEmployeeNames';
import { CLAIM_STATUS_COLOR, claimStatusLabel, formatAmount } from '../../utils/expenseDisplay';
import { formatDate } from '../../utils/dateUtils';
import ScrollableTable from '../common/ScrollableTable';
import SearchableSelect from '../common/SearchableSelect';
import DepartmentPicker, { useDepartmentOptions } from '../costcentre/DepartmentPicker'; // B4-H
import ProjectSelect, { projectLabel, projectTagLabel, useProjectOptions } from '../project/ProjectSelect'; // P1-A project
import { AuthService } from '../../services/auth.service'; // B4-H

interface Props {
    claim: ExpenseClaimDTO;
    open: boolean;
    onClose: () => void;
    onResolved: () => void;
    isFinalTier: boolean;
}

const ExpenseClaimApprovalDialog: React.FC<Props> = ({ claim: initial, open, onClose, onResolved, isFinalTier }) => {
    const [claim, setClaim] = useState<ExpenseClaimDTO>(initial);
    const [categories, setCategories] = useState<ExpenseCategoryDTO[]>([]);
    const [editing, setEditing] = useState(false);
    const [method, setMethod] = useState<ReimbursementMethod>('STANDALONE');
    const [rejectReason, setRejectReason] = useState('');
    const [busy, setBusy] = useState(false);
    const toast = useToast();
    const departmentOptions = useDepartmentOptions(AuthService.getSelectedInstitution()); // B4-H
    const projectOptions = useProjectOptions(AuthService.getSelectedInstitution()); // P1-A project
    const projectName = (id?: string | null, code?: string | null, name?: string | null) => { const p = id ? projectOptions.find(o => o.id === id) : undefined; return p ? projectLabel(p) : (code ? projectTagLabel(code, name) : (id ? id.slice(0, 8) : '—')); };
    const employeeName = useEmployeeNames([claim]);

    useEffect(() => {
        setClaim(initial);
    }, [initial]);

    // Categories are only needed once the approver switches to line adjustment.
    useEffect(() => {
        if (open && editing && categories.length === 0) loadCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, editing]);

    const loadCategories = async () => {
        try {
            const page = await ExpenseCategoryService.listByInstitution(0, 200);
            setCategories(page.content.filter(c => c.isActive));
        } catch (e: any) {
            toast.showApiError(e, 'Failed to load categories');
        }
    };

    const handleConflict = (e: any, fallback: string) => {
        const data = e?.response?.data;
        const msg = data?.message ?? (typeof data === 'string' ? data : null);
        toast.showError(msg ?? fallback);
    };

    const handleLineEdit = async (lineId: string, patch: Partial<{ amount: number; description: string; categoryId: string; expenseDate: string; departmentId: string; projectId: string; clearProject: boolean }>) => {
        const line = claim.lines.find(l => l.id === lineId);
        if (!line) return;
        setBusy(true);
        try {
            const updated = await ExpenseClaimService.editLine(claim.id, lineId, {
                expenseDate: patch.expenseDate ?? line.expenseDate,
                categoryId: patch.categoryId ?? line.categoryId,
                description: patch.description ?? line.description ?? null,
                amount: patch.amount ?? line.amount,
                receiptDocumentId: line.receiptDocumentId ?? null,
                ...(patch.departmentId ? { departmentId: patch.departmentId } : {}), // B4-H: only when the approver changed it
                ...(patch.projectId ? { projectId: patch.projectId } : {}), // P1-A project: only when the approver changed it
                ...(patch.clearProject ? { clearProject: true } : {}), // P1-A: the approver chose "(no project)"
            });
            setClaim(updated);
            toast.showSuccess('Line updated');
        } catch (e: any) {
            handleConflict(e, 'Failed to update line');
        } finally {
            setBusy(false);
        }
    };

    const handleLineRemove = async (lineId: string) => {
        if (!window.confirm('Remove this line from the claim?')) return;
        setBusy(true);
        try {
            const updated = await ExpenseClaimService.removeLine(claim.id, lineId);
            setClaim(updated);
            toast.showSuccess('Line removed');
        } catch (e: any) {
            handleConflict(e, 'Failed to remove line');
        } finally {
            setBusy(false);
        }
    };

    const handleApprove = async () => {
        setBusy(true);
        try {
            await ExpenseClaimService.approve(claim.id, isFinalTier ? method : undefined);
            toast.showSuccess(isFinalTier ? 'Claim approved' : 'Approved — moved to next tier');
            onResolved();
            onClose();
        } catch (e: any) {
            handleConflict(e, 'Approval failed');
        } finally {
            setBusy(false);
        }
    };

    const handleReject = async () => {
        if (!rejectReason.trim()) {
            toast.showError('Please provide a reason for rejection');
            return;
        }
        setBusy(true);
        try {
            await ExpenseClaimService.reject(claim.id, rejectReason.trim());
            toast.showSuccess('Claim rejected');
            onResolved();
            onClose();
        } catch (e: any) {
            handleConflict(e, 'Rejection failed');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-help-page="expense.approval.page">
            <DialogTitle>
                <Stack direction="row" spacing={2} alignItems="center">
                    <span>{claim.title}</span>
                    <Chip size="small" label={claimStatusLabel(claim.status)} color={CLAIM_STATUS_COLOR[claim.status]} />
                    {claim.currentApprovalTier != null && (
                        <Chip size="small" label={`Tier ${claim.currentApprovalTier}`} color="warning" />
                    )}
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Claimant</Typography>
                        <Typography>{employeeName(claim)}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Period</Typography>
                        <Typography>{formatDate(claim.periodFrom)} → {formatDate(claim.periodTo)}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Total</Typography>
                        <Typography variant="h6">{formatAmount(claim.totalAmount)}</Typography>
                    </Box>
                </Stack>

                <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                    <Button size="small" data-help="expense.approval.adjust" variant={editing ? 'contained' : 'outlined'} onClick={() => setEditing(e => !e)} disabled={busy}>
                        {editing ? 'Done adjusting' : 'Adjust lines'}
                    </Button>
                </Stack>
                <ScrollableTable>
                    <Table size="small" data-help-section="expense.approval.lines">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="expense.claimLines.col.date">Date</TableCell>
                                <TableCell data-help="expense.claimLines.col.category">Category</TableCell>
                                <TableCell data-help="expense.claimLines.col.description">Description</TableCell>
                                {departmentOptions.length > 0 && <TableCell data-help="expense.claimLines.col.department">Department</TableCell>}{/* B4-H */}{projectOptions.length > 0 && <TableCell data-help="expense.claimLines.col.project">Project</TableCell>}
                                <TableCell align="right" data-help="expense.claimLines.col.amount">Amount</TableCell>
                                {editing && <TableCell />}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {claim.lines.map(l => (editing ? (
                                <TableRow key={l.id}>
                                    <TableCell>
                                        <TextField
                                            data-help="expense.approval.edit.date" type="date" size="small" defaultValue={l.expenseDate}
                                            onBlur={e => {
                                                if (e.target.value !== l.expenseDate) {
                                                    handleLineEdit(l.id, { expenseDate: e.target.value });
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <SearchableSelect<ExpenseCategoryDTO>
                                            options={categories}
                                            value={l.categoryId}
                                            onChange={id => handleLineEdit(l.id, { categoryId: id })}
                                            getOptionLabel={c => c.name}
                                            data-help="expense.approval.edit.category" label="Category"
                                            sx={{ minWidth: 140 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            data-help="expense.approval.edit.description" size="small" defaultValue={l.description ?? ''} fullWidth
                                            onBlur={e => {
                                                if (e.target.value !== (l.description ?? '')) {
                                                    handleLineEdit(l.id, { description: e.target.value });
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    {departmentOptions.length > 0 && (
                                        <TableCell sx={{ minWidth: 160 }}>{/* B4-H */}
                                            <DepartmentPicker
                                                options={departmentOptions} value={l.departmentId ?? ''} size="small"
                                                data-help="expense.approval.edit.department" label="Department" placeholder="(claimant's)"
                                                onChange={id => { if (id && id !== l.departmentId) handleLineEdit(l.id, { departmentId: id }); }}
                                            />
                                        </TableCell>
                                    )}
                                    {projectOptions.length > 0 && (
                                        <TableCell sx={{ minWidth: 160 }}>{/* P1-A project */}
                                            <ProjectSelect
                                                options={projectOptions} value={l.projectId ?? ''} size="small" data-help="expense.approval.edit.project" label="Project"
                                                currentLabel={l.projectId ? projectName(l.projectId, l.projectCode, l.projectName) : null}
                                                placeholder="(no project)" data-testid={`approve-project-${l.id}`}
                                                onChange={id => {
                                                    if (!id) { if (l.projectId) handleLineEdit(l.id, { clearProject: true }); }
                                                    else if (id !== l.projectId) handleLineEdit(l.id, { projectId: id });
                                                }}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell align="right">
                                        <TextField
                                            data-help="expense.approval.edit.amount" type="number" size="small" defaultValue={l.amount}
                                            inputProps={{ min: 0, step: '0.01' }}
                                            sx={{ width: 110 }}
                                            onBlur={e => {
                                                const next = Number(e.target.value);
                                                if (Number.isFinite(next) && next !== Number(l.amount)) {
                                                    handleLineEdit(l.id, { amount: next });
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton data-help="expense.approval.action.removeLine"
                                            size="small" onClick={() => handleLineRemove(l.id)}
                                            disabled={busy || claim.lines.length === 1}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <TableRow key={l.id}>
                                    <TableCell data-help="expense.claimLines.col.date" sx={{ whiteSpace: 'nowrap' }}>{formatDate(l.expenseDate)}</TableCell>
                                    <TableCell data-help="expense.claimLines.col.category">{l.categoryName ?? '—'}</TableCell>
                                    <TableCell data-help="expense.claimLines.col.description" sx={{ whiteSpace: 'normal', minWidth: 140 }}>{l.description ?? '—'}</TableCell>
                                    {departmentOptions.length > 0 && (
                                        <TableCell data-help="expense.claimLines.col.department">{departmentOptions.find(d => d.id === l.departmentId)?.name ?? '—'}</TableCell>
                                    )}{/* B4-H */}
                                    {projectOptions.length > 0 && <TableCell data-help="expense.claimLines.col.project">{projectName(l.projectId, l.projectCode, l.projectName)}</TableCell>}{/* P1-A project */}
                                    <TableCell align="right" data-help="expense.claimLines.col.amount">{formatAmount(l.amount)}</TableCell>
                                </TableRow>
                            )))}
                        </TableBody>
                    </Table>
                </ScrollableTable>

                {isFinalTier && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>Reimbursement method (final tier)</Typography>
                        <FormControl data-help="expense.approval.method">
                            <RadioGroup row value={method} onChange={e => setMethod(e.target.value as ReimbursementMethod)}>
                                <FormControlLabel value="STANDALONE" control={<Radio />} label="Standalone payment" />
                                <FormControlLabel value="PAYROLL_ATTACH" control={<Radio />} label="Attach to payroll" />
                            </RadioGroup>
                        </FormControl>
                    </>
                )}

                <Divider sx={{ my: 2 }} />
                <TextField data-help="expense.approval.reason"
                    label="Reason (required if rejecting)" fullWidth multiline rows={2}
                    value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button data-help="expense.approval.close" onClick={onClose} disabled={busy}>Close</Button>
                <Button color="error" variant="outlined" data-help="expense.approval.reject" onClick={handleReject} disabled={busy}>Reject</Button>
                <Button variant="contained" data-help="expense.approval.approve" onClick={handleApprove} disabled={busy}>
                    {isFinalTier ? 'Final approve' : 'Approve'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExpenseClaimApprovalDialog;
